import { useCallback, useEffect, useRef } from "react";
import { reaction } from "mobx";

import { useIdleSafeGuard } from "./useChatWebSocketListeners/useIdleSafeGuard";

import { useChatWidgetContext } from "@/components/Playground/Drawer/Chat/context";
import { usePlayground } from "@/components/Playground/playground-store";
import { useInfiniteGetPlaygroundSessionChatMessages } from "@/queries/playground/useInfiniteGetPlaygroundSessionChatMessages";

const DEFAULT_CHAT_MESSAGES_PER_PAGE = 20;
//TODO use as constant for ID selector and pass id into the hook
const DATA_SCROLL_CONTAINER_SELECTOR = "[data-chat-scroll-container]";

export const useHydrateChatMessages = () => {
  const { variableSetIndex } = useChatWidgetContext();
  const { chatStore, workspace_id, instanceId, isSessionModeChat, isInitializing } =
    usePlayground();

  useIdleSafeGuard();

  const hasInitialized = useRef(false);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useInfiniteGetPlaygroundSessionChatMessages({
      workspaceId: workspace_id,
      sessionId: instanceId,
      variableSetIndex,
      perPage: DEFAULT_CHAT_MESSAGES_PER_PAGE,
      enabled: isSessionModeChat && !isInitializing,
    });

  // Initialize messages and stats from first page
  useEffect(() => {
    if (data?.pages && data.pages.length > 0 && !hasInitialized.current) {
      const page = data.pages[0];
      // Backend returns pages in desc order (page 1 = newest batch) but each page's
      // messages are in chronological order. Reverse the pages array so older pages
      // come first, then flatten to get all messages in chronological order.
      const messages = [...data.pages].reverse().flatMap(_page => _page.messages);

      chatStore.setMessages(variableSetIndex, messages ?? []);
      chatStore.setStats(variableSetIndex, {
        total_messages: page?.total_messages ?? 0,
        total_cost: page?.total_cost ?? 0,
        total_input_tokens: page?.total_input_tokens ?? 0,
        total_latency_ms: page?.total_latency_ms ?? 0,
        total_output_tokens: page?.total_output_tokens ?? 0,
      });

      hasInitialized.current = true;
    }
  }, [data?.pages, chatStore, variableSetIndex]);

  // Reset initialization flag when session changes
  useEffect(() => {
    hasInitialized.current = false;
  }, [instanceId]);

  // Watch for rehydration requests (e.g. after importing chat history from a request).
  // When the generation counter bumps, reset hasInitialized so the data effect above
  // re-processes the next query result, then force a refetch.
  useEffect(() => {
    const dispose = reaction(
      () => chatStore.getRehydrationGeneration(variableSetIndex),
      () => {
        hasInitialized.current = false;
        refetch();
      },
    );
    return () => dispose();
  }, [chatStore, variableSetIndex, refetch]);

  const loadMoreMessages = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    const scrollContainer =
      document.querySelector(
        `${DATA_SCROLL_CONTAINER_SELECTOR}[data-variable-set-index="${variableSetIndex}"]`,
      ) ?? document.querySelector(DATA_SCROLL_CONTAINER_SELECTOR);

    const currentScrollHeight = scrollContainer?.scrollHeight ?? 0;

    fetchNextPage().then(result => {
      const newPage = result.data?.pages[result.data.pages.length - 1];
      if (newPage?.messages) {
        // Backend returns messages in chronological order within each page, so prepend directly
        chatStore.prependMessages(variableSetIndex, newPage.messages);

        // Maintain scroll position after prepending
        requestAnimationFrame(() => {
          const container =
            document.querySelector(
              `${DATA_SCROLL_CONTAINER_SELECTOR}[data-variable-set-index="${variableSetIndex}"]`,
            ) ?? document.querySelector(DATA_SCROLL_CONTAINER_SELECTOR);

          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - currentScrollHeight;
          }
        });
      }
    });
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, chatStore, variableSetIndex]);

  return {
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: hasNextPage ?? false,
    total: data?.pages[0]?.total_messages ?? 0,
    loadMoreMessages,
    refetch,
  };
};
